// Captura y conversión de audio a PCM para el reconocimiento tipo Shazam
// (Req. 12.2). La API de Shazam (RapidAPI, endpoint `songs/v2/detect`) NO acepta
// contenedores (webm/opus/mp3/wav): exige PCM RAW en base64, 44100 Hz, 1 canal
// (mono), 16-bit con signo, little-endian, y un clip corto (<500KB; 3-5s basta).
//
// `MediaRecorder` produce webm/opus (o mp4 en Safari), que Shazam rechaza. Por eso
// convertimos a PCM con la Web Audio API. Hay dos caminos, uno fallback del otro:
//   1. PRINCIPAL (`startPcmCapture`): captura las muestras crudas EN VIVO desde el
//      micrófono con un ScriptProcessorNode. Es el más fiable en Safari/iOS porque
//      no depende de decodificar un contenedor.
//   2. FALLBACK (`decodeBlobToPcm`): si la captura en vivo no está disponible o da
//      muy pocas muestras, decodifica el blob grabado por MediaRecorder con
//      `decodeAudioData` y lo convierte igual.
// Ambos terminan en el MISMO formato (PCM 44100/mono/16-bit LE) listo para enviar.

/** Frecuencia de muestreo exigida por Shazam. */
const TARGET_SAMPLE_RATE = 44100;

/** `AudioContext` tolerante a prefijos (Safari antiguo usa `webkitAudioContext`). */
type AudioCtxCtor = new (options?: AudioContextOptions) => AudioContext;
function getAudioContextCtor(): AudioCtxCtor | undefined {
  const w = window as unknown as {
    AudioContext?: AudioCtxCtor;
    webkitAudioContext?: AudioCtxCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext;
}

/** Controlador de una captura PCM en vivo (opción 2). */
export interface PcmCapture {
  /**
   * Detiene la captura, libera recursos y devuelve el PCM base64 (44100/mono/16).
   * Devuelve `null` si no se capturaron muestras suficientes (p. ej. el navegador
   * no permitió el ScriptProcessor), para que el llamador caiga al fallback.
   */
  stop(): Promise<string | null>;
  /** Aborta la captura liberando recursos sin producir resultado. */
  cancel(): void;
}

/**
 * Inicia la captura PCM EN VIVO desde un `MediaStream` del micrófono (opción 2).
 * Acumula las muestras del primer canal; al `stop()` las re-muestrea a 44100 Hz y
 * las codifica a PCM 16-bit LE en base64. Lanza si la Web Audio API no existe.
 */
export function startPcmCapture(stream: MediaStream): PcmCapture {
  const Ctor = getAudioContextCtor();
  if (!Ctor) throw new Error('Web Audio API no disponible.');

  const ctx = new Ctor();
  const source = ctx.createMediaStreamSource(stream);
  // Buffer de 4096 muestras: compromiso entre latencia y nº de callbacks. Mono
  // de entrada y de salida (solo nos interesa un canal).
  const processor = ctx.createScriptProcessor(4096, 1, 1);
  const chunks: Float32Array[] = [];
  let total = 0;

  processor.onaudioprocess = (e: AudioProcessingEvent) => {
    const input = e.inputBuffer.getChannelData(0);
    // Copiar: el buffer interno se reutiliza entre callbacks.
    const copy = new Float32Array(input.length);
    copy.set(input);
    chunks.push(copy);
    total += copy.length;
  };

  source.connect(processor);
  // En algunos navegadores el ScriptProcessor no "corre" si no está conectado a
  // un destino. Lo conectamos a destination; es inaudible porque no re-emitimos.
  processor.connect(ctx.destination);

  const sampleRate = ctx.sampleRate;
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    try { processor.disconnect(); } catch { /* ya desconectado */ }
    try { source.disconnect(); } catch { /* ya desconectado */ }
    void ctx.close().catch(() => undefined);
  };

  return {
    async stop(): Promise<string | null> {
      release();
      // Umbral mínimo: ~0.3s de audio. Menos que eso no sirve para reconocer y es
      // señal de que la captura en vivo no funcionó: que el llamador use el fallback.
      if (total < sampleRate * 0.3) return null;
      const merged = mergeChunks(chunks, total);
      const resampled = resampleTo(merged, sampleRate, TARGET_SAMPLE_RATE);
      return floatToPcm16Base64(resampled);
    },
    cancel: release,
  };
}

/**
 * FALLBACK (opción 1): decodifica un blob de audio (lo que grabó MediaRecorder) y
 * lo convierte a PCM 44100/mono/16-bit LE en base64. Lanza si no se puede decodificar
 * (Safari a veces no decodifica su propio contenedor; por eso es el fallback).
 */
export async function decodeBlobToPcm(blob: Blob): Promise<string> {
  const Ctor = getAudioContextCtor();
  if (!Ctor) throw new Error('Web Audio API no disponible.');

  const arrayBuffer = await blob.arrayBuffer();
  const ctx = new Ctor();
  try {
    // `decodeAudioData` con Promise; algunos Safari solo soportan la forma con
    // callbacks, así que la envolvemos.
    const audioBuffer = await decodeAudioData(ctx, arrayBuffer);
    const mono = downmixToMono(audioBuffer);
    const resampled = resampleTo(mono, audioBuffer.sampleRate, TARGET_SAMPLE_RATE);
    return floatToPcm16Base64(resampled);
  } finally {
    void ctx.close().catch(() => undefined);
  }
}

/** `decodeAudioData` compatible con la forma callback (Safari) y Promise. */
function decodeAudioData(ctx: AudioContext, data: ArrayBuffer): Promise<AudioBuffer> {
  return new Promise<AudioBuffer>((resolve, reject) => {
    const maybePromise = ctx.decodeAudioData(data, resolve, reject);
    // En navegadores modernos devuelve una Promise; la encadenamos por si acaso.
    if (maybePromise && typeof (maybePromise as Promise<AudioBuffer>).then === 'function') {
      (maybePromise as Promise<AudioBuffer>).then(resolve, reject);
    }
  });
}

/** Promedia todos los canales a uno solo (mono). */
function downmixToMono(buffer: AudioBuffer): Float32Array {
  const length = buffer.length;
  const channels = buffer.numberOfChannels;
  if (channels === 1) return buffer.getChannelData(0).slice();
  const out = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    let sum = 0;
    for (let ch = 0; ch < channels; ch++) sum += buffer.getChannelData(ch)[i] ?? 0;
    out[i] = sum / channels;
  }
  return out;
}

/** Concatena los trozos Float32 acumulados en un único buffer contiguo. */
function mergeChunks(chunks: Float32Array[], total: number): Float32Array {
  const out = new Float32Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

/**
 * Re-muestrea una señal mono de `from` Hz a `to` Hz con interpolación lineal.
 * Suficiente para fingerprinting (Shazam no necesita calidad de estudio) y evita
 * depender de `OfflineAudioContext` (menos fiable en iOS para esto).
 */
function resampleTo(samples: Float32Array, from: number, to: number): Float32Array {
  if (from === to) return samples;
  const ratio = from / to;
  const outLength = Math.floor(samples.length / ratio);
  const out = new Float32Array(outLength);
  for (let i = 0; i < outLength; i++) {
    const pos = i * ratio;
    const idx = Math.floor(pos);
    const frac = pos - idx;
    const a = samples[idx] ?? 0;
    const b = samples[idx + 1] ?? a;
    out[i] = a + (b - a) * frac;
  }
  return out;
}

/** Máximo de muestras que caben bajo el límite de ~500KB de Shazam. */
const MAX_SAMPLES = 250000; // 250000 * 2 bytes = 500000 bytes < 500KB (~5.6s @44100).

/**
 * Convierte muestras Float32 [-1,1] a PCM 16-bit con signo little-endian y las
 * codifica a base64 (sin cabecera: Shazam quiere RAW). La grabación puede durar
 * hasta 15s, pero Shazam solo acepta ~5.6s (<500KB), así que en vez de recortar
 * los primeros segundos se envía la VENTANA de ~5.6s con MÁS ENERGÍA (la parte
 * con música más clara, evitando silencios/ruido de arranque) — mejora la tasa
 * de reconocimiento sin exceder el límite.
 */
function floatToPcm16Base64(samples: Float32Array): string {
  const window = pickBestWindow(samples, MAX_SAMPLES);
  const n = window.length;
  const buffer = new ArrayBuffer(n * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < n; i++) {
    let s = window[i] ?? 0;
    if (s > 1) s = 1; else if (s < -1) s = -1;
    // Escala asimétrica estándar: positivos *0x7FFF, negativos *0x8000.
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true /* little-endian */);
  }
  return arrayBufferToBase64(buffer);
}

/**
 * Selecciona la ventana contigua de hasta `maxSamples` con mayor energía (suma de
 * cuadrados). Si la señal ya cabe, la devuelve entera. Escanea en pasos de ~0.5s
 * usando una suma acumulada de cuadrados para que sea O(n) aunque la grabación
 * sea larga (15s).
 */
function pickBestWindow(samples: Float32Array, maxSamples: number): Float32Array {
  if (samples.length <= maxSamples) return samples;

  // Suma de prefijos de cuadrados: prefix[i] = Σ sample[0..i-1]². Permite calcular
  // la energía de cualquier ventana en O(1).
  const prefix = new Float64Array(samples.length + 1);
  for (let i = 0; i < samples.length; i++) {
    const v = samples[i] ?? 0;
    prefix[i + 1] = (prefix[i] ?? 0) + v * v;
  }

  const step = Math.max(1, Math.floor(TARGET_SAMPLE_RATE / 2)); // ~0.5s
  let bestStart = 0;
  let bestEnergy = -1;
  for (let start = 0; start + maxSamples <= samples.length; start += step) {
    const energy = (prefix[start + maxSamples] ?? 0) - (prefix[start] ?? 0);
    if (energy > bestEnergy) {
      bestEnergy = energy;
      bestStart = start;
    }
  }
  return samples.subarray(bestStart, bestStart + maxSamples);
}

/** Codifica un ArrayBuffer a base64 por bloques (evita desbordar el call stack). */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.subarray(i, i + CHUNK);
    binary += String.fromCharCode.apply(null, slice as unknown as number[]);
  }
  return btoa(binary);
}
